import type { ReactNode } from 'react'

import { ArrowUpRight } from 'lucide-react'
import { buttonClass } from '@/lib/ui'
import { FadeLink } from '@/components/generals/fade-link'
import { Reveal, RevealLines } from '@/components/generals/reveal'
import { VisitMap } from '@/components/visit/visit-map'
import { OBSERVATORY, OPENING_HOURS, formatCoordinate } from '@/constants/observatory'

export const VISIT_FACTS = [
    {
        title: 'Getting here',
        text: 'Up the lane from the B3224, a mile north-west of Wheddon Cross. Postcodes cover miles of moor here, so follow the brown observatory signs or use the coordinates.'
    },
    {
        title: 'Parking',
        text: 'Free parking for forty cars and two coaches, with low red lighting. Please drive the last half mile on sidelights after dark: your headlights blind the telescopes.'
    },
    {
        title: 'Accessibility',
        text: 'Step-free from the car park to the dome, the exhibition and the café. The roof deck has a lift. Wheelchair spaces in the dome, a hearing loop at the desk, and assistance dogs welcome.'
    },
    {
        title: 'Dress for the roof',
        text: 'It is several degrees colder up here than in town and the deck is open to the wind. Bring a hat, gloves and one more layer than you think you need, even in summer.'
    }
]

/** Address, hours and the practical things, with a quiet map. */
export const VisitSection = (): ReactNode => {
    return (
        <section id='visit' aria-labelledby='visit-title' className='relative py-28 md:py-36'>
            <div className='container-page grid gap-14 lg:grid-cols-12 lg:gap-16'>
                <div className='lg:col-span-5'>
                    <p className='label-mono text-brass'>
                        Visit
                    </p>
                    <RevealLines
                        id='visit-title'
                        lines={['Up on', 'Hollowmoor Ridge']}
                        className='display-serif mt-5 text-5xl text-star md:text-7xl'
                    />

                    <Reveal className='mt-10'>
                        <address className='not-italic'>
                            <p className='text-lg text-star'>
                                {OBSERVATORY.name}
                            </p>
                            {OBSERVATORY.addressLines.map(line => (
                                <p key={line} className='text-star-2'>
                                    {line}
                                </p>
                            ))}
                            <p className='data-mono mt-3 text-star-3'>
                                {`${formatCoordinate(OBSERVATORY.latitude, 'N', 'S')}   ${formatCoordinate(OBSERVATORY.longitude, 'E', 'W')}`}
                            </p>
                        </address>
                    </Reveal>

                    <Reveal className='mt-10'>
                        <p className='label-mono text-star-3'>
                            Opening hours
                        </p>
                        <dl className='mt-3'>
                            {OPENING_HOURS.map(row => (
                                <div key={row.days} className='grid grid-cols-[1fr_auto] gap-4 border-b border-hair py-3'>
                                    <dt>
                                        <span className='block text-star'>
                                            {row.days}
                                        </span>
                                        <span className='block text-sm text-star-3'>
                                            {row.note}
                                        </span>
                                    </dt>
                                    <dd className='data-mono text-star-2'>
                                        {row.hours}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </Reveal>

                    <div className='mt-10 flex flex-wrap items-center gap-4'>
                        <a href={OBSERVATORY.directionsUrl} target='_blank' rel='noreferrer' className={buttonClass('primary')}>
                            Get directions
                            <ArrowUpRight aria-hidden className='size-4' strokeWidth={1.25} />
                        </a>
                        <FadeLink href='/visit' className={buttonClass('quiet', 'h-12')}>
                            Plan your visit
                        </FadeLink>
                    </div>
                </div>

                <div className='lg:col-span-7'>
                    <Reveal>
                        <VisitMap />
                    </Reveal>

                    <div className='mt-10 grid gap-8 sm:grid-cols-2'>
                        {VISIT_FACTS.map((fact, index) => (
                            <Reveal key={fact.title} delay={index * 80} className='border-t border-hair pt-5'>
                                <h3 className='font-heading text-2xl text-star'>
                                    {fact.title}
                                </h3>
                                <p className='mt-2 text-[0.95rem] leading-relaxed text-star-2'>
                                    {fact.text}
                                </p>
                            </Reveal>
                        ))}
                    </div>

                    <p className='label-mono mt-12 flex items-center gap-3 text-star-2'>
                        <span aria-hidden className='size-2 rounded-full bg-signal' />
                        Dark sky site. Please use red torches only.
                    </p>
                </div>
            </div>
        </section>
    )
}
